import { useMemo, useState } from "react";
import { Box, IconButton, Tooltip } from "@mui/material";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { useNavigate } from "react-router-dom";
import { ProposalProps } from "../../interfaces/interfaces";
import { useDate } from "../../hooks";
import { ListColumn, ListPageLayout } from "../shared/ListPageLayout";

interface Props {
  proposals: ProposalProps[];
  tallies: ProposalProps[];
}

// `ProposalProps` (interfaces.ts) is stale relative to what the API actually
// returns for a well's technical proposals / tally designs. The original
// DataGrid columns already relied on `id`/`customName`/`date` (via untyped
// `params.row` access), so this local type documents the real shape used
// here without touching the shared interface (out of this task's scope).
interface WellDetailRow {
  id: number | string;
  customName: string;
  date: string;
}

const DEFAULT_PAGE_SIZE = 10;

// Sorts the FULL array using the same accessor a column declares via
// `sortAccessor`, before pagination slices it — mirrors ListPageLayout's own
// internal (uncontrolled) sort algorithm exactly, but is invoked here, over
// the complete filtered dataset, because these two grids page a fully-loaded
// local array rather than a single already-paginated server page. A well can
// have more proposals/tallies than fit on one page (OSI_server_2's
// `getWellDetail` applies no limit), so sorting only the already-sliced page
// (ListPageLayout's uncontrolled default) would silently leave the rest of
// the dataset unsorted relative to the visible page.
function sortRows<T>(
  rows: T[],
  sortBy: string | null,
  sortDir: "asc" | "desc",
  columns: ListColumn<T>[],
): T[] {
  if (!sortBy) return rows;
  const col = columns.find((c) => c.key === sortBy);
  if (!col?.sortAccessor) return rows;
  return [...rows].sort((a, b) => {
    const av = col.sortAccessor!(a);
    const bv = col.sortAccessor!(b);
    if (av == null && bv == null) return 0;
    if (av == null) return 1;
    if (bv == null) return -1;
    if (av < bv) return sortDir === "asc" ? -1 : 1;
    if (av > bv) return sortDir === "asc" ? 1 : -1;
    return 0;
  });
}

export const TableWellDetail = ({ proposals, tallies }: Props) => {
  const navigate = useNavigate();
  const { getDateFromString } = useDate();

  const techRows = useMemo(
    () => proposals as unknown as WellDetailRow[],
    [proposals],
  );
  const tallyRows = useMemo(
    () => tallies as unknown as WellDetailRow[],
    [tallies],
  );

  const [techSearch, setTechSearch] = useState("");
  const [techPage, setTechPage] = useState(0);
  const [techPageSize, setTechPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [techSortBy, setTechSortBy] = useState<string | null>(null);
  const [techSortDir, setTechSortDir] = useState<"asc" | "desc">("asc");

  const [tallySearch, setTallySearch] = useState("");
  const [tallyPage, setTallyPage] = useState(0);
  const [tallyPageSize, setTallyPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [tallySortBy, setTallySortBy] = useState<string | null>(null);
  const [tallySortDir, setTallySortDir] = useState<"asc" | "desc">("asc");

  const handleTechSortChange = (key: string) => {
    setTechSortDir((prev) => (techSortBy === key && prev === "asc" ? "desc" : "asc"));
    setTechSortBy(key);
  };

  const handleTallySortChange = (key: string) => {
    setTallySortDir((prev) => (tallySortBy === key && prev === "asc" ? "desc" : "asc"));
    setTallySortBy(key);
  };

  const techColumns: ListColumn<WellDetailRow>[] = [
    {
      key: "customName",
      header: "Technical Proposal",
      render: (r) => r.customName,
      sortAccessor: (r) => r.customName,
      width: "2fr",
    },
    {
      key: "date",
      header: "Date",
      render: (r) => getDateFromString(r.date),
      sortAccessor: (r) => getDateFromString(r.date),
      width: "1fr",
    },
  ];

  const tallyColumns: ListColumn<WellDetailRow>[] = [
    {
      key: "customName",
      header: "Tally Design",
      render: (r) => r.customName,
      sortAccessor: (r) => r.customName,
      width: "2fr",
    },
    {
      key: "date",
      header: "Date",
      render: (r) => getDateFromString(r.date),
      sortAccessor: (r) => getDateFromString(r.date),
      width: "1fr",
    },
  ];

  const filteredTech = useMemo(() => {
    const q = techSearch.trim().toLowerCase();
    return q
      ? techRows.filter((r) => r.customName?.toLowerCase().includes(q))
      : techRows;
  }, [techRows, techSearch]);

  const filteredTally = useMemo(() => {
    const q = tallySearch.trim().toLowerCase();
    return q
      ? tallyRows.filter((r) => r.customName?.toLowerCase().includes(q))
      : tallyRows;
  }, [tallyRows, tallySearch]);

  // Sort the FULL filtered array first (see sortRows' comment above for why),
  // THEN slice to the current page — so paging through a sorted list is
  // correct relative to the whole dataset, not just whatever rows happened to
  // land on that page beforehand.
  const sortedTech = sortRows(filteredTech, techSortBy, techSortDir, techColumns);
  const sortedTally = sortRows(filteredTally, tallySortBy, tallySortDir, tallyColumns);

  const pagedTech = sortedTech.slice(
    techPage * techPageSize,
    techPage * techPageSize + techPageSize,
  );
  const pagedTally = sortedTally.slice(
    tallyPage * tallyPageSize,
    tallyPage * tallyPageSize + tallyPageSize,
  );

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: "24px" }}>
      <ListPageLayout<WellDetailRow>
        title="Technical Proposals"
        columns={techColumns}
        rows={pagedTech}
        getRowId={(r) => r.id}
        rowsLoading={false}
        searchValue={techSearch}
        onSearchChange={setTechSearch}
        searchPlaceholder="Search technical proposals…"
        sortBy={techSortBy}
        sortDir={techSortDir}
        onSortChange={handleTechSortChange}
        page={techPage}
        pageSize={techPageSize}
        totalCount={filteredTech.length}
        onPageChange={setTechPage}
        onPageSizeChange={(pageSize) => {
          setTechPageSize(pageSize);
          setTechPage(0);
        }}
        rowActions={(r) => (
          <Tooltip title="Detail">
            <IconButton
              onClick={() => navigate(`/tech/${r.id}`)}
              size="small"
            >
              <OpenInNewIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      />
      <ListPageLayout<WellDetailRow>
        title="Tally Designs"
        columns={tallyColumns}
        rows={pagedTally}
        getRowId={(r) => r.id}
        rowsLoading={false}
        searchValue={tallySearch}
        onSearchChange={setTallySearch}
        searchPlaceholder="Search tally designs…"
        sortBy={tallySortBy}
        sortDir={tallySortDir}
        onSortChange={handleTallySortChange}
        page={tallyPage}
        pageSize={tallyPageSize}
        totalCount={filteredTally.length}
        onPageChange={setTallyPage}
        onPageSizeChange={(pageSize) => {
          setTallyPageSize(pageSize);
          setTallyPage(0);
        }}
        rowActions={(r) => (
          <Tooltip title="Detail">
            <IconButton
              onClick={() => navigate(`/tally/${r.id}`)}
              size="small"
            >
              <OpenInNewIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      />
    </Box>
  );
};
